// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';

import Products from 'Components/Products';
import ProductFactory from 'Containers/ProductFactory';
import useFilters from 'Hooks/useFilters';
import useUser from 'Hooks/useUser';
import useRouter from 'Hooks/useRouter';
import { GRID_VIEW_MODE, VIEW_MODES } from 'Constants';
import { UIContext } from 'Contexts/UIContext';
import { ProductsContext } from 'Contexts/ProductsContext';
import { CartContext } from 'Contexts/CartContext';
import useToastNotifications from 'Hooks/useToastNotifications';
import ProductListItem from 'Containers/Products/ProductListItem';
import ProductGridItem from 'Containers/Products/ProductGridItem';
import ProductTableItem from 'Containers/Products/ProductTableItem';
import useScreen from 'Hooks/useScreen';

ProductFactory.register('list', ProductListItem);
ProductFactory.register('grid', ProductGridItem);
ProductFactory.register('table', ProductTableItem);

const ProductsContainer = memo(() => {
  const { data, isLoading: productsLoading, error } = useContextSelector(
    ProductsContext,
    d => d
  );
  const { data: cart, updateProducts: updateCartProducts } = useContextSelector(
    CartContext,
    d => d
  );
  const { data: user } = useUser();
  const uiStateViewMode = useContextSelector(UIContext, d => d.uiState?.viewMode || GRID_VIEW_MODE);
  const { t } = useTranslation();
  const { query } = useRouter();
  const { activeFilters, setMobileFiltersOpen } = useFilters();
  const theme = useTheme();
  const screen = useScreen();
  const isMobile = screen.width > 0 && screen.width <= 767;
  const [quantities, setQuantities] = useState({});
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const { addNotification } = useToastNotifications();
  const [viewMode, setViewMode] = useState(
    !isMobile && VIEW_MODES.includes(query.ref)
      ? query.ref
      : uiStateViewMode
  );

  const getViewMode = useCallback(() => {
    if (isMobile) {
      return GRID_VIEW_MODE;
    }

    return VIEW_MODES.includes(query.ref)
      ? query.ref
      : uiStateViewMode || GRID_VIEW_MODE;
  }, [isMobile, query.ref, uiStateViewMode]);

  const addAllToCart = async () => {
    const productsToAdd = (data?.products || []).reduce((acc, product) => {
      if (
        !product.stockValue
        || !(product.id in quantities)
        || !quantities[product.id]
        || quantities[product.id] < product.minQuantity
      ) return acc;

      const qty = quantities[product.id];
      const existingCartItem = (cart?.items || [])
        .find(item => item.productId === product.id && !item.promotion);
      if (existingCartItem) {
        if (existingCartItem.quantity + qty > product.stockValue) {
          addNotification({
            type: 'warning',
            title: `${ t('products.insufficientStock') } for ${ t('products.product').toLowerCase() }: ${ product.code }`
          });
        } else {
          acc.push({
            ...existingCartItem,
            ...product,
            quantity: Math.min(
              product.stockValue,
              Math.max(product.minQuantity || 1, existingCartItem.quantity + qty)
            )
          });
        }
      } else {
        acc.push(({
          ...product,
          quantity: qty
        }));
      }
      return acc;
    }, []);
    if (!productsToAdd.length) return;

    setAddToCartLoading(true);
    try {
      const response = await updateCartProducts(productsToAdd);
      if (response === null) {
        addNotification({
          type: 'warning',
          title: t('products.dropShipmentWarning')
        });
      } else {
        addNotification({
          title: t('cart.successfullyAddedProductsToCart')
        });
      }
      setQuantities(prevQty => Object
        .entries(prevQty)
        .reduce((qtyAcc, [id]) => {
          // eslint-disable-next-line no-param-reassign
          qtyAcc[id] = 0;
          return qtyAcc;
        }, {}));
    } catch (err) {
      addNotification({
        title: t('cart.failedAddingProductsToCart'),
        type: 'error'
      });
    } finally {
      setAddToCartLoading(false);
    }
  };

  const afterChangeQty = useCallback(prod => {
    setQuantities(prevQty => ({
      ...prevQty,
      [prod.id]: prod.quantity
    }));
  }, []);

  const afterAddToCart = useCallback(prod => {
    setQuantities(prevQty => {
      if (prod.id in prevQty) {
        // eslint-disable-next-line no-param-reassign
        delete prevQty[prod.id];
        return { ...prevQty };
      }
      return prevQty;
    });
  }, []);

  useEffect(() => {
    setViewMode(getViewMode());
  }, [getViewMode]);

  useEffect(() => {
    if (data?.products) {
      setQuantities({});
    }
  }, [data?.products]);

  useEffect(() => {
    setQuantities({});
  }, [viewMode]);

  const memoizedProducts = useMemo(
    () => (data?.products || [])
      .map(product => (
        <ProductFactory
          key={ product.id }
          type={ ProductFactory.has(viewMode) ? viewMode : GRID_VIEW_MODE }
          product={ {
            ...product,
            // eslint-disable-next-line no-nested-ternary
            quantity: product.id in quantities
              ? quantities[product.id]
              : viewMode === 'table' ? 0 : product.minQuantity
          } }
          onChangeQty={ afterChangeQty }
          onAddToCart={ afterAddToCart }
        />
      )),
    [data?.products, quantities, viewMode]
  );

  return (
    <Products
      onAddToCart={ addAllToCart }
      viewMode={ viewMode }
      addToCartLoading={ addToCartLoading }
      productsLoading={ productsLoading }
      t={ t }
      theme={ theme }
      activeFilters={ activeFilters || {} }
      error={ error?.info?.error || error?.message || data?.error }
      products={ data?.products || [] }
      quantities={ quantities }
      isRecentlyAdded={ user?.isRecentlyAdded }
      pricesAvailableToday={ user?.pricesAvailableToday }
      setMobileFiltersOpen={ setMobileFiltersOpen }
    >
      { memoizedProducts }
    </Products>
  );
});

ProductsContainer.displayName = 'ProductsContainer';

export default ProductsContainer;
